import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { RecipeLayer } from "@/pages/sy/models/RecipeLayer";
import {
  LAYER_MOVE_MODE,
  type LayerMoveMode,
} from "@/pages/sy/models/LayerMoveMode";
import { useReorderRecipeLayerMutation } from "@/pages/sy/hooks/queries/useRecipeMutations";
import { getLayerMovePreview } from "@/pages/sy/utils/layerGroups";

type PendingMove = {
  layer: RecipeLayer;
  targetLayerNumber: number;
};

type Params = {
  layers: RecipeLayer[];
  setNotificationMessage: (message: string) => void;
  onError: (err: unknown) => void;
};

export const useRecipeLayerMove = ({
  layers,
  setNotificationMessage,
  onError,
}: Params) => {
  const queryClient = useQueryClient();
  const reorderLayerMutation = useReorderRecipeLayerMutation();
  const [pendingMove, setPendingMove] = useState<PendingMove | null>(null);

  const executeMove = (
    layer: RecipeLayer,
    layerNumber: number,
    mode: LayerMoveMode,
    onDone?: () => void,
  ) => {
    if (!layer.RecipeLayerId) {
      onDone?.();
      return;
    }

    reorderLayerMutation.mutate(
      {
        recipeLayerId: layer.RecipeLayerId,
        newLayerNumber: layerNumber,
        mode,
      },
      {
        onSuccess: () => {
          setNotificationMessage(
            mode === LAYER_MOVE_MODE.MOVE_INTO
              ? `${layer.CommodityName ?? "Material"} moved into layer ${layerNumber}`
              : `Layer reordered to position ${layerNumber}`,
          );
        },
        onError,
        onSettled: async () => {
          await queryClient.invalidateQueries({ queryKey: ["recipe-layers"] });
          setPendingMove(null);
          onDone?.();
        },
      },
    );
  };

  const reorderLayer = (
    layer: RecipeLayer,
    layerNumber: number,
    onDone?: () => void,
  ) => executeMove(layer, layerNumber, LAYER_MOVE_MODE.REORDER, onDone);

  const requestMove = (layer: RecipeLayer, targetLayerNumber: number) => {
    if (targetLayerNumber === layer.LayerNumber) return;

    const targetExists = layers.some(
      (other) =>
        other.LayerNumber === targetLayerNumber &&
        other.RecipeLayerId !== layer.RecipeLayerId,
    );

    if (!targetExists) {
      reorderLayer(layer, targetLayerNumber);
      return;
    }

    setPendingMove({ layer, targetLayerNumber });
  };

  const preview = pendingMove
    ? getLayerMovePreview(
        layers,
        pendingMove.layer,
        pendingMove.targetLayerNumber,
        "RecipeLayerId",
      )
    : null;

  const dialogProps = {
    open: pendingMove !== null,
    materialName: pendingMove?.layer.CommodityName ?? "",
    targetLayerNumber: pendingMove?.targetLayerNumber ?? 0,
    targetMaterialNames:
      preview?.targetMaterials.map((layer) => layer.CommodityName ?? "") ?? [],
    reorderLayerNumber: pendingMove?.targetLayerNumber ?? 0,
    moveIntoLayerNumber: preview?.moveIntoLayerNumber ?? 0,
    isCommodityInTarget: preview?.isCommodityInTarget ?? false,
    isPending: reorderLayerMutation.isPending,
    onReorder: () =>
      pendingMove &&
      reorderLayer(pendingMove.layer, pendingMove.targetLayerNumber),
    onMoveInto: () =>
      pendingMove &&
      executeMove(
        pendingMove.layer,
        pendingMove.targetLayerNumber,
        LAYER_MOVE_MODE.MOVE_INTO,
      ),
    onClose: () => setPendingMove(null),
  };

  return { requestMove, reorderLayer, dialogProps };
};
